import * as React from "react";

import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/utils";
import { AreaMap, type MapVenue } from "@/pages/areas/components/area-map";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import { useCityVenuePins } from "@/pages/areas/hooks/use-city-venue-pins";
import {
  UNPARENTED_COLOUR,
  useAreaColours,
} from "@/pages/areas/lib/part-colours";
import { parseCityKey } from "@/types/area";
import type { Area, AreaTreeNode } from "@/types/area";

const NO_AREAS: Area[] = [];
const NO_PINS: MapVenue[] = [];

const Row = ({
  node,
  colour,
  selected,
  indent,
  onPick,
}: {
  node: AreaTreeNode;
  /** The shape's colour on the map, from the same `areaColours` call. */
  colour: string;
  selected: boolean;
  indent?: boolean;
  onPick: (id: string) => void;
}) => (
  <button
    type="button"
    onClick={() => onPick(node.id)}
    aria-pressed={selected}
    className={cn(
      "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      indent && "pl-6",
      selected && "bg-muted font-medium",
    )}
  >
    <span
      aria-hidden
      data-testid={`legend-${node.id}`}
      className={cn(
        "shrink-0",
        indent ? "h-2.5 w-2.5 rounded-full" : "h-3 w-3 rounded-sm",
      )}
      style={{ background: colour }}
    />
    <span className="flex-1 truncate">{node.name}</span>
    <span className="ml-2 text-xs text-muted-foreground">
      {node.venueCount}
    </span>
  </button>
);

/**
 * The Map tab: the city's part outlines, area shapes and venue pins, with the tree beside it.
 *
 * Height: the viewport less the app header (48px), the main padding (2 x 8px), the toolbar row
 * (the `TabsList` is h-10, 40px) and the list view's gap (4px): 108px, the same
 * `calc(100vh-108px)` the venue, artist and genre previews use. `vh` rather than `dvh` to match
 * them: IMS is a desktop tool, where the two are equal. The tree scrolls inside that height.
 * Picking the same row again zooms back to it.
 *
 * Picking a row in the tree highlights the shape and zooms to it. Clicking a shape on the map
 * highlights it and opens its popup, without moving the map. Parts are outlines only: a click on
 * a part's empty space reaches no shape and does nothing; pick the part in the tree to zoom to it.
 */
export const AreasMapTab = ({ cityKey }: { cityKey: string | undefined }) => {
  const repo = useAreaRepository();
  const [highlightId, setHighlightId] = React.useState<string>();
  const [focusId, setFocusId] = React.useState<string>();
  /** Counts tree picks, so picking the same row again zooms back to it. */
  const [fitRequest, setFitRequest] = React.useState(0);
  const parsed = cityKey ? parseCityKey(cityKey) : undefined;

  const areas = useQuery({
    queryKey: ["areas", "map-areas", cityKey],
    enabled: !!parsed,
    queryFn: () => repo.cityAreas(parsed!.city, parsed!.countryCode),
  });
  const tree = useQuery({
    queryKey: ["areas", "map-tree", cityKey],
    enabled: !!parsed,
    queryFn: () => repo.tree(parsed!.city, parsed!.countryCode),
  });
  const venues = useCityVenuePins(parsed?.city, parsed?.countryCode);

  const areaList = areas.data ?? NO_AREAS;
  const colours = useAreaColours(areaList);
  const colourOf = (id: string) => colours.get(id) ?? UNPARENTED_COLOUR;

  React.useEffect(() => {
    setHighlightId(undefined);
    setFocusId(undefined);
  }, [cityKey]);

  const pick = React.useCallback((id: string) => {
    setHighlightId(id);
    setFocusId(id);
    setFitRequest((n) => n + 1);
  }, []);

  if (!parsed) return <p className="text-muted-foreground">Pick a city</p>;

  const error = areas.error ?? tree.error ?? venues.error;

  return (
    <div
      data-testid="areas-map-tab"
      className="flex h-[calc(100vh-108px)] min-h-[420px] gap-3"
    >
      <aside className="w-64 shrink-0 space-y-2 overflow-y-auto pr-1">
        {tree.data?.parts.map(({ part, children, totalVenueCount }) => (
          <div key={part.id}>
            <Row
              node={{ ...part, venueCount: totalVenueCount }}
              colour={colourOf(part.id)}
              selected={highlightId === part.id}
              onPick={pick}
            />
            {children.map((c) => (
              <Row
                key={c.id}
                node={c}
                colour={colourOf(c.id)}
                indent
                selected={highlightId === c.id}
                onPick={pick}
              />
            ))}
          </div>
        ))}
        {!!tree.data?.unparented.length && (
          <div>
            <div className="px-2 text-xs uppercase text-muted-foreground">
              No part
            </div>
            {tree.data.unparented.map((n) => (
              <Row
                key={n.id}
                node={n}
                colour={colourOf(n.id)}
                selected={highlightId === n.id}
                onPick={pick}
              />
            ))}
          </div>
        )}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {error && (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : "Could not load the map"}
          </p>
        )}
        <AreaMap
          areas={areaList}
          venues={venues.data?.pins ?? NO_PINS}
          totalVenues={venues.data?.total}
          highlightId={highlightId}
          onAreaClick={setHighlightId}
          fitTo={focusId ?? "all"}
          fitRequest={fitRequest}
          colours={colours}
          className="h-auto min-h-0 flex-1"
        />
      </div>
    </div>
  );
};
